// ============================================================================
// Bid step 3 — the rules behind the screen, kept pure so a test can hold them.
// Built to Ben's approved demo (docs/build-room/demos/item-841.html, 2026-10-15).
// ⛔ No pricing arithmetic here: every dollar figure comes from the database's
// bid_price_lines(). This file only decides which rate a line starts from, which
// badge it wears, and what stops the bid being priced.
// ============================================================================

import type { BidRow } from './api';

/** The approved floor-type list (the database checks the same five). */
export const FLOOR_TYPES = ['VCT', 'Sheet vinyl / LVT', 'Sealed concrete', 'Ceramic tile', 'Carpet'] as const;

export const FREQUENCIES: { times: number; label: string }[] = [
  { times: 1, label: 'Annual' },
  { times: 2, label: 'Biannual' },
  { times: 4, label: 'Quarterly' },
  { times: 12, label: 'Monthly' },
];
export function frequencyLabel(times: number): string {
  return FREQUENCIES.find((f) => f.times === times)?.label ?? `${times} times a year`;
}

/** Each floor-care service and the setting its baseline lives under. Burnish and grout have none. */
export const RATE_KEY: Record<string, string | null> = {
  STRIP_WAX: 'floor_care_strip_wax_per_sf',
  SCRUB_RECOAT: 'floor_care_autoscrub_wax_per_sf',
  AUTO_SCRUB: 'floor_care_auto_scrub_per_sf',
  BURNISH: null,
  GROUT: null,
  CARPET_CLEAN: 'floor_care_carpet_clean_per_sf',
};

/** Where a service's pre-filled rate came from, for this bid's market. */
export interface Baseline { value: number | null; fromMarket: boolean }

export interface Line {
  line_id?: string;
  floor_type: string;
  service_code: string;
  sqft: number | null;
  rate: number | null;
  times_per_year: number;
  edited: boolean;
}

export interface Answers {
  cons: boolean | null;
  consMode: 'pct_of_monthly' | 'cost_plus' | 'other';
  pct: number;          // percent as a person types it: 15 for 15%
  markup: number;       // 20 for 20%
  otherAmt: string;
  otherDesc: string;
  chem: boolean | null;
  floor: boolean | null;
  floorMode: 'baked_in' | 'billed_separately';
  lines: Line[];
  card: Record<string, number>; // rate-card rates typed for this bid, by service
  /** the rate card as saved: each service's line id, and the saved figure of a rate NOT typed for this bid */
  cardSaved: Record<string, { line_id: string; rate: number | null }>;
}

export interface Bounds { pctDefault: number; pctMin: number; pctMax: number; markupDefault: number; markupMin: number; markupMax: number }

/** The market as the screen says it: "St. Louis Metro" → "St. Louis". */
export function marketShort(market: string | null | undefined): string {
  return (market ?? '').replace(/ Metro$/, '');
}

/** The rate a rate-card service shows: typed for this bid, else as saved, else today's baseline. */
export function cardRate(a: Answers, code: string, base: Baseline | undefined): number | null {
  if (a.card[code] !== undefined) return a.card[code];
  return a.cardSaved[code]?.rate ?? base?.value ?? null;
}

/** The rate a line shows: its own when typed for this bid, else the baseline. */
export function lineRate(l: Line, base: Baseline | undefined): number | null {
  if (l.edited) return l.rate;
  return l.rate ?? base?.value ?? null;
}

export type BadgeKind = 'edited' | 'market' | 'default' | 'none';
export function badgeKind(edited: boolean, base: Baseline | undefined): BadgeKind {
  if (edited) return 'edited';
  if (!base || base.value == null) return 'none';
  return base.fromMarket ? 'market' : 'default';
}
export function badgeText(kind: BadgeKind, market: string | null | undefined): string {
  const m = marketShort(market);
  if (kind === 'edited') return 'edited for this bid';
  if (kind === 'none') return 'no figure anywhere · enter a rate';
  if (kind === 'market') return `${m} baseline`;
  return `default · no ${m} figure`;
}

/** The answers a bid opens with: what is saved, else Ben's starting point for the arrangements. */
export function answersFromBid(bid: BidRow, lines: Line[], bounds: Bounds, withEmptyLine = true): Answers {
  const baked = lines.filter((l) => l.floor_type);
  const card: Record<string, number> = {};
  const cardSaved: Answers['cardSaved'] = {};
  for (const l of lines) {
    if (l.floor_type || l.rate == null) continue;
    // a typed rate lives in `card`; clearing it goes back to the baseline, never to itself
    if (l.line_id) cardSaved[l.service_code] = { line_id: l.line_id, rate: l.edited ? null : l.rate };
    if (l.edited) card[l.service_code] = l.rate;
  }
  return {
    cons: bid.consumables_in_contract,
    consMode: (bid.consumables_arrangement as Answers['consMode']) ?? 'pct_of_monthly',
    pct: bid.consumables_pct != null ? round2(Number(bid.consumables_pct) * 100) : bounds.pctDefault,
    markup: bid.consumables_markup_pct != null ? round2(Number(bid.consumables_markup_pct) * 100) : bounds.markupDefault,
    otherAmt: bid.consumables_other_monthly != null ? String(bid.consumables_other_monthly) : '',
    otherDesc: bid.consumables_other_description ?? '',
    chem: bid.chemicals_in_contract,
    floor: bid.floor_care_in_contract,
    floorMode: (bid.floor_care_arrangement as Answers['floorMode']) ?? 'baked_in',
    // "one empty line": floor type and service from the top of each list, biannual, no square feet yet
    lines: baked.length || !withEmptyLine ? baked : [emptyLine()],
    card,
    cardSaved,
  };
}
export function emptyLine(): Line {
  return { floor_type: 'VCT', service_code: 'STRIP_WAX', sqft: null, rate: null, times_per_year: 2, edited: false };
}
/** The demo's "+ Add a floor-care line". */
export function addedLine(): Line {
  return { floor_type: 'Carpet', service_code: 'CARPET_CLEAN', sqft: 1500, rate: null, times_per_year: 4, edited: false };
}
function round2(n: number): number { return Math.round(n * 100) / 100; }

export function clamp(v: number, lo: number, hi: number, fallback: number): number {
  if (!Number.isFinite(v)) return fallback;
  return Math.min(hi, Math.max(lo, v));
}

/** What stops the bid being priced, in the demo's words. Empty = it can be saved. */
export function problems(a: Answers, bases: Record<string, Baseline>): string[] {
  const out: string[] = [];
  if (a.cons == null || a.chem == null || a.floor == null) out.push('unanswered');
  if (a.cons && a.consMode === 'other' && (!a.otherAmt || !a.otherDesc.trim())) out.push('other');
  if (a.floor && a.floorMode === 'baked_in') {
    if (a.lines.some((l) => lineRate(l, bases[l.service_code]) == null)) out.push('no_rate');
    if (a.lines.some((l) => l.sqft == null || !(l.sqft > 0))) out.push('no_sqft');
  }
  return out;
}

/** The answers and lines as the database's price and save doors take them. */
export function toDraft(a: Answers, bases: Record<string, Baseline>, services: string[]) {
  const services_ = {
    consumables_in_contract: a.cons,
    consumables_arrangement: a.cons ? a.consMode : null,
    consumables_pct: a.cons && a.consMode === 'pct_of_monthly' ? a.pct / 100 : null,
    consumables_markup_pct: a.cons && a.consMode === 'cost_plus' ? a.markup / 100 : null,
    consumables_other_monthly: a.cons && a.consMode === 'other' && a.otherAmt !== '' ? Number(a.otherAmt) : null,
    consumables_other_description: a.cons && a.consMode === 'other' ? a.otherDesc.trim() || null : null,
    chemicals_in_contract: a.chem,
    floor_care_in_contract: a.floor,
    floor_care_arrangement: a.floor ? a.floorMode : null,
  };
  let lines: Record<string, unknown>[] = [];
  if (a.floor && a.floorMode === 'baked_in') {
    lines = a.lines.map((l) => ({
      line_id: l.line_id, service_code: l.service_code, floor_type: l.floor_type, sqft: l.sqft,
      rate_per_sf: lineRate(l, bases[l.service_code]), times_per_year: l.times_per_year, rate_edited: l.edited,
    }));
  } else if (a.floor && a.floorMode === 'billed_separately') {
    lines = services
      .map((code) => {
        const edited = a.card[code] !== undefined;
        const rate = cardRate(a, code, bases[code]);
        return rate == null ? null : {
          line_id: a.cardSaved[code]?.line_id, service_code: code, floor_type: null, sqft: null,
          rate_per_sf: rate, times_per_year: null, rate_edited: edited,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
  }
  return { services: services_, lines };
}

// ---------------------------------------------------------------------------
// "The bid, as the customer will see it" — the approved panel's words. The dollar
// figures come in from bid_price_lines(); this only says which lines show and how.
// ---------------------------------------------------------------------------

export interface CustomerView {
  consLabel: string | null;
  floorIncluded: boolean;
  floorDetail: string;
  side: string[];
  errors: string[];
}

export function customerView(a: Answers, serviceName: (code: string) => string, bases: Record<string, Baseline>): CustomerView {
  const consLabel = !a.cons ? null
    : a.consMode === 'pct_of_monthly' ? `Consumables (${a.pct}% of nightly)`
    : a.consMode === 'other' ? `Consumables, other${a.otherDesc.trim() ? `: ${a.otherDesc.trim()}` : ''}`
    : null;
  const floorIncluded = !!a.floor && a.floorMode === 'baked_in';
  const floorDetail = a.lines.map((l) => `${serviceName(l.service_code)} ${frequencyLabel(l.times_per_year).toLowerCase()}`).join(' · ');
  const side: string[] = [];
  if (a.cons && a.consMode === 'cost_plus') side.push(`Consumables: invoiced at cost + ${a.markup}% as supplied`);
  if (a.cons === false) side.push('Consumables: customer supplies');
  if (a.chem === true) side.push('Chemicals: TCS supplies (included)');
  if (a.chem === false) side.push('Chemicals: customer supplies');
  if (a.floor && a.floorMode === 'billed_separately') side.push('Periodic floor care: per-visit rates, billed as performed');
  if (a.floor === false) side.push('No periodic floor care');
  const p = problems(a, bases);
  const errors: string[] = [];
  if (p.includes('unanswered')) errors.push('Answer Yes or No for consumables, chemicals and periodic floor care.');
  if (p.includes('other')) errors.push('Consumables "Other" is incomplete.');
  if (p.includes('no_rate')) errors.push('A floor-care line has no rate.');
  if (p.includes('no_sqft')) errors.push('A floor-care line has no square feet.');
  return { consLabel, floorIncluded, floorDetail, side, errors };
}
